import React, { useState } from 'react';
import moment from 'moment';

import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { updateApprovalRequiredOrder, rejectApprovalRequiredOrder, saveMedicineQuantities } from '../../services/api/get-pharmacy-orders';

// Palette + cell styles kept consistent with PharmacyCart.js (the sibling cart view).
const BRAND = '#6C5DD3';
const BRAND_LIGHT = '#F5F4FF';
const BRAND_BORDER = '#E5E9FE';
const TEXT_PRIMARY = '#0F0B28';
const TEXT_SECONDARY = '#585969';
const TEXT_MUTED = '#A5A6BB';
const BORDER = '#E3E3E3';
const GREEN = '#1eb865';
const RED = '#EB5757';

const th = {
  padding: '6px 10px',
  fontSize: '10px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: TEXT_SECONDARY,
  background: BRAND_LIGHT,
  borderBottom: `1px solid ${BRAND_BORDER}`,
  whiteSpace: 'nowrap',
};

const td = {
  padding: '6px 10px',
  fontSize: '12px',
  color: TEXT_PRIMARY,
  borderBottom: '1px solid #F5F5F5',
};

const sectionLabel = {
  fontSize: '10px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  color: TEXT_MUTED,
  margin: '14px 0 8px',
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const rupee = (v) => (v === null || v === undefined || v === '' ? '—' : `₹${v}`);
const dateTime = (v) => (v ? moment(v).format('DD MMM YYYY, hh:mm A') : '—');

// Current (possibly Ops-edited) quantity for a medicine — local edit wins, else the DB quantity.
const currentQty = (m, quantities = {}) =>
  m.cartItemId in quantities ? num(quantities[m.cartItemId]) : num(m.quantity);

// True while a stepped quantity has not been persisted via Update Cart yet.
const hasUnsavedEdits = (medicines = [], quantities = {}) =>
  medicines.some((m) => m.cartItemId in quantities && num(quantities[m.cartItemId]) !== num(m.quantity));

// "Sum of Cost" = Σ (discounted_price × current quantity) over all medicines.
const sumOfCost = (medicines = [], quantities = {}) =>
  medicines.reduce((s, m) => s + num(m.discountedPrice) * currentQty(m, quantities), 0);

// Labelled detail cell (mirrors the label/value fields used in the sibling views).
const Field = ({ label, value, color }) => (
  <td style={{ ...td, width: '25%' }}>
    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: TEXT_MUTED }}>{label}</div>
    <div style={{ color: color || TEXT_PRIMARY }}>{value === null || value === undefined || value === '' ? '—' : value}</div>
  </td>
);

function CartReviewPanel({ order, quantities, setQty, onUpdateCart, saving, updateMsg }) {
  const medicines = order.medicines || [];
  const total = sumOfCost(medicines, quantities);

  const paymentFields = [
    { label: 'User Paid', value: rupee(order.userPaid), color: RED },
    { label: 'Wallet Deduction', value: rupee(order.walletDeduction), color: BRAND },
    { label: 'Total Amount (DB: Sum of Cost)', value: `₹${total.toFixed(2)}`, color: GREEN },
    { label: 'Order Type', value: order.orderType || '—', color: TEXT_SECONDARY },
  ];

  const stepBtn = {
    width: 26,
    height: 26,
    border: `1px solid ${BORDER}`,
    background: '#fff',
    color: TEXT_SECONDARY,
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 700,
    lineHeight: '22px',
  };

  return (
    <div style={{ padding: '14px' }}>
      <div style={sectionLabel}>Cart Items (As per DB)</div>
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>Medicine Name (Drug ID)</th>
              <th style={th}>MRP</th>
              <th style={th}>Discounted Price</th>
              <th style={th}>Qty</th>
              <th style={th}>Total MRP (mrp × qty)</th>
              <th style={th}>Cost (disc × qty)</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {medicines.length === 0 && (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: TEXT_MUTED, padding: '24px' }}>No items in cart</td></tr>
            )}
            {medicines.map((m, index) => {
              const qty = currentQty(m, quantities);
              const maxQty = num(m.quantity); // saved quantity is the ceiling — reductions are final once saved
              const rowTotalMrp = num(m.mrp) * qty;
              const rowCost = num(m.discountedPrice) * qty;
              return (
                <tr key={m.cartItemId} style={{ background: index % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <td style={{ ...td, color: TEXT_MUTED, fontWeight: 600 }}>{index + 1}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{m.drugName}<div style={{ color: TEXT_MUTED, fontWeight: 400 }}>Drug ID: {m.drugId}</div></td>
                  <td style={td}>{rupee(m.mrp)}</td>
                  <td style={{ ...td, color: GREEN, fontWeight: 600 }}>{rupee(m.discountedPrice)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{qty}</td>
                  <td style={td}>₹{rowTotalMrp.toFixed(2)}<div style={{ color: TEXT_MUTED }}>({num(m.mrp)} × {qty})</div></td>
                  <td style={{ ...td, fontWeight: 700, color: BRAND }}>₹{rowCost.toFixed(2)}<div style={{ color: TEXT_MUTED, fontWeight: 400 }}>({num(m.discountedPrice)} × {qty})</div></td>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button type="button" style={stepBtn} disabled={qty <= 0}
                        onClick={() => setQty(order.cartId, m.cartItemId, qty - 1)}>−</button>
                      <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{qty}</span>
                      <button type="button" style={stepBtn} disabled={qty >= maxQty}
                        onClick={() => setQty(order.cartId, m.cartItemId, Math.min(maxQty, qty + 1))}>+</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={sectionLabel}>Payment Breakdown (As per DB)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${BORDER}` }}>
        <tbody>
          <tr>
            {paymentFields.map((f) => <Field key={f.label} label={f.label} value={f.value} color={f.color} />)}
          </tr>
        </tbody>
      </table>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
        <span style={{ fontSize: '12px', color: TEXT_MUTED }}>Review the medicines and modify quantity where required.</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {updateMsg && (
            <span style={{ fontSize: '12px', color: updateMsg.type === 'success' ? GREEN : RED }}>
              {updateMsg.text}
            </span>
          )}
          <Button color="primary" disabled={saving} onClick={() => onUpdateCart(order)}>
            {saving ? 'Saving…' : 'Update Cart'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function StarPharmacyOrder({
  isOpen,
  tableSource,
  getOrdersForApprovals,
  onClose,
  cancelationReasons = [],
}) {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedCartId, setSelectedCartId] = useState(null);
  const [selectedReasonIndex, setSelectedReasonIndex] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState(null);

  // { [cartId]: { [cartItemId]: quantity } } — Ops-edited quantities (seeded from DB quantity on interaction).
  const [quantitiesByCart, setQuantitiesByCart] = useState({});

  const [expanded, setExpanded] = useState({});

  // cartId currently being saved via Update Cart (for button feedback), and last save message.
  const [savingCartId, setSavingCartId] = useState(null);
  const [updateMsg, setUpdateMsg] = useState(null);

  // cartId currently being approved (for button feedback), and last approve message.
  const [approvingCartId, setApprovingCartId] = useState(null);
  const [approveMsg, setApproveMsg] = useState(null);

  const setQty = (cartId, cartItemId, quantity) =>
    setQuantitiesByCart((prev) => ({ ...prev, [cartId]: { ...(prev[cartId] || {}), [cartItemId]: Math.max(0, quantity) } }));

  // Persist Ops-edited medicine quantities to the cart.
  const onUpdateCart = async (order) => {
    const edited = quantitiesByCart[order.cartId] || {};
    const medicines = order.medicines || [];
    // Send every medicine's current quantity (edited value if changed, else its DB quantity).
    const items = medicines.map((m) => ({
      cartItemId: m.cartItemId,
      quantity: m.cartItemId in edited ? Math.max(0, Number(edited[m.cartItemId]) || 0) : Number(m.quantity) || 0
    }));

    setSavingCartId(order.cartId);
    setUpdateMsg(null);
    try {
      const res = await saveMedicineQuantities(order.cartId, items);
      if (res.success) {
        setUpdateMsg({ cartId: order.cartId, type: 'success', text: 'Cart updated successfully' });
        await getOrdersForApprovals();
      } else {
        setUpdateMsg({ cartId: order.cartId, type: 'error', text: res.message || 'Failed to update cart' });
      }
    } catch (err) {
      setUpdateMsg({ cartId: order.cartId, type: 'error', text: err.message || 'Failed to update cart' });
    } finally {
      setSavingCartId(null);
    }
  };

  const onApprovedOrder = (cartId) => async () => {
    // Approving reads the cart from the DB, so pending edits would be silently dropped.
    const order = (tableSource || []).find((o) => o.cartId === cartId);
    if (order && hasUnsavedEdits(order.medicines, quantitiesByCart[cartId])) {
      setApproveMsg({ cartId, type: 'error', text: 'Save the quantity changes with Update Cart before approving' });
      return;
    }

    setApprovingCartId(cartId);
    setApproveMsg(null);
    try {
      const res = await updateApprovalRequiredOrder(cartId);
      if (res && res.success === false) {
        setApproveMsg({ cartId, type: 'error', text: res.message || 'Failed to approve' });
      } else {
        setApproveMsg({ cartId, type: 'success', text: 'Order approved successfully' });
        await getOrdersForApprovals();
      }
    } catch (err) {
      setApproveMsg({ cartId, type: 'error', text: err.message || 'Failed to approve' });
    } finally {
      setApprovingCartId(null);
    }
  };

  const handleSideEffects = () => {
    setShowRejectModal(false);
    setSelectedReasonIndex('');
    setComment('');
    setSelectedCartId(null);
    setError(null);
  };

  const onConfirmReject = async () => {
    try {
      if (selectedReasonIndex === '') {
        alert('Please select a cancellation reason');
        return;
      }
      const reason = cancelationReasons[selectedReasonIndex];
      if (reason.requiresText && !comment.trim()) {
        alert('Please enter reason text');
        return;
      }
      const cancelComment = reason.requiresText ? comment.trim() : reason.label;

      const rejectResponse = await rejectApprovalRequiredOrder(selectedCartId, cancelComment);
      if (rejectResponse.success) {
        handleSideEffects();
      } else if (rejectResponse.message) {
        setError(rejectResponse.message);
        return;
      }
      await getOrdersForApprovals();
    } catch (err) {
      handleSideEffects();
    }
  };

  const onRejectOrder = (cartId) => async () => {
    try {
      if (cancelationReasons.length > 0) {
        setSelectedCartId(cartId);
        setShowRejectModal(true);
        return;
      }
      await rejectApprovalRequiredOrder(cartId);
      await getOrdersForApprovals();
    } catch (err) {
      console.log('err', err);
    }
  };

  const columns = [
    { Header: 'Cart ID', accessor: 'cartId', minWidth: 70 },
    { Header: 'Order ID', accessor: 'orderId', minWidth: 70, Cell: (r) => r.value || '-' },
    { Header: 'Partner', accessor: 'partner', minWidth: 80, Cell: (r) => r.value || '-' },
    { Header: 'Patient Name', accessor: 'patientName', minWidth: 90, Cell: (r) => r.value || '-' },
    { Header: 'Phone', accessor: 'phoneNumber', minWidth: 90, Cell: (r) => r.value || '-' },
    { Header: 'User Name', accessor: 'userName', minWidth: 90 },
    { Header: 'Sponsor', accessor: 'sponsor', minWidth: 100 },
    { Header: 'Order Date & Time', accessor: 'orderDateTime', minWidth: 110, Cell: (r) => dateTime(r.value) },
    {
      Header: 'Total (Sum of Cost)',
      id: 'sumOfCost',
      minWidth: 90,
      accessor: (d) => sumOfCost(d.medicines, quantitiesByCart[d.cartId]),
      Cell: (r) => `₹${num(r.value).toFixed(2)}`,
    },
    {
      Header: 'Actions',
      sortable: false,
      minWidth: 180,
      Cell: (r) => {
        const cartId = r.original.cartId;
        const approving = approvingCartId === cartId;
        const unsaved = hasUnsavedEdits(r.original.medicines, quantitiesByCart[cartId]);
        const msg = approveMsg && approveMsg.cartId === cartId ? approveMsg : null;
        return (
          <div>
            <div className="d-flex" style={{ gap: 8 }}>
              <button
                className="btn btn-success"
                disabled={approving || unsaved}
                title={unsaved ? 'Save the quantity changes with Update Cart before approving' : ''}
                onClick={onApprovedOrder(cartId)}
              >
                {approving ? 'Approving…' : 'Approve'}
              </button>
              <button className="btn btn-danger" onClick={onRejectOrder(cartId)}>Reject</button>
            </div>
            {unsaved && !msg && (
              <div style={{ fontSize: '12px', marginTop: 4, color: TEXT_MUTED }}>
                Unsaved quantity changes — use Update Cart
              </div>
            )}
            {msg && (
              <div style={{ fontSize: '12px', marginTop: 4, color: msg.type === 'success' ? GREEN : RED }}>
                {msg.text}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen} style={{ maxWidth: '1100px', width: '95%', borderRadius: '4px' }}>
        <ModalHeader style={{ backgroundColor: '#8752CC' }} toggle={onClose}>
          <h3 className="text-center" style={{ color: '#fff', fontWeight: 'bold' }}>
            Star ₹2K+ Cart – Medicine Level Review
          </h3>
        </ModalHeader>
        <ModalBody>
          <ReactTable
            data={tableSource || []}
            columns={columns}
            pageSize={20}
            className="-striped -highlight"
            expanded={expanded}
            onExpandedChange={(newExpanded) => setExpanded(newExpanded)}
            SubComponent={(row) => (
              <CartReviewPanel
                order={row.original}
                quantities={quantitiesByCart[row.original.cartId] || {}}
                setQty={setQty}
                onUpdateCart={onUpdateCart}
                saving={savingCartId === row.original.cartId}
                updateMsg={updateMsg && updateMsg.cartId === row.original.cartId ? updateMsg : null}
              />)}
          />
        </ModalBody>
      </Modal>

      <Modal isOpen={showRejectModal}>
        <ModalHeader toggle={() => handleSideEffects()}>Reject Order</ModalHeader>
        <ModalBody>
          <Label>Cancellation Reason</Label>
          <Input
            type="select"
            value={selectedReasonIndex}
            onChange={(e) => {
              setSelectedReasonIndex(e.target.value);
              setComment('');
            }}
          >
            <option value="">Select reason</option>
            {cancelationReasons.map((reason, index) => (
              <option key={reason.label} value={index}>{reason.label}</option>
            ))}
          </Input>

          {selectedReasonIndex !== '' && cancelationReasons[selectedReasonIndex]?.requiresText && (
            <>
              <Label className="mt-3">Comment</Label>
              <Input
                type="textarea"
                maxLength={255}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter reason (max 255 characters)"
              />
              <small className="text-muted">{comment.length}/255</small>
            </>
          )}
          {error && <p className="text-danger mt-2">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => handleSideEffects()}>Cancel</Button>
          <Button color="danger" onClick={onConfirmReject}>Reject Order</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
